import React from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/layout/NavBar';
import SiteFooter from '../components/layout/SiteFooter';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import FAQSection from '../components/FAQSection';
import ShowcaseSection from '../components/ShowcaseSection';
import { useWaitlistModal } from '../context/WaitlistModalContext';
import { webPageSchema, breadcrumbSchema, faqSchema } from '../schema';

const faqItems = [
  { question: 'What is a smart disinfectant doormat?', answer: 'A smart disinfectant doormat is an entryway device that uses sensors and an automated disinfectant delivery system to sanitize shoe soles as someone steps on it, unlike a traditional mat which only traps loose dirt.' },
  { question: 'How does SaniQ detect when someone steps on it?', answer: 'SaniQ uses a pressure-sensor array embedded in the mat surface that detects exact foot and shoe placement, triggering targeted disinfectant delivery only where contact occurs.' },
  { question: 'How long does disinfection take?', answer: 'A full disinfection cycle takes 2 to 3 seconds per step, designed to fit into a normal walking pace with no need to pause or wait.' },
  { question: 'Does SaniQ replace hand sanitizing or surface cleaning?', answer: 'No. SaniQ addresses one specific vector, shoe-sole contamination at the entry point, and is designed to complement, not replace, hand hygiene and regular surface cleaning.' },
  { question: 'Is SaniQ safe for pets and children?', answer: 'SaniQ is being developed with medical-grade, low-residue disinfectant formulations intended for high-traffic residential and commercial entryways; full safety documentation will be published ahead of launch.' },
  { question: 'Can SaniQ be used outdoors?', answer: 'Yes, SaniQ is being built to an IP67 weatherproof rating, intended to withstand rain, sun, and dust at outdoor entrances.' },
  { question: 'What is the difference between UV sanitizing mats and spray disinfection mats?', answer: 'UV mats use ultraviolet light exposure, which typically requires a longer dwell time and has line-of-sight limitations on textured shoe soles; spray disinfection mats like SaniQ apply liquid disinfectant directly to the sole surface for faster, more consistent contact-based elimination.' },
  { question: 'Is SaniQ available for commercial and healthcare settings?', answer: 'SaniQ is planning both residential and commercial editions, including configurations suited to healthcare, childcare, and hospitality entryways, ahead of its 2027 Australian launch.' },
];

const Features = () => {
  const { openWaitlistModal } = useWaitlistModal();

  return (
    <div className="relative w-full min-h-screen">
      <SEO
        title="Features: How SaniQ's Smart Disinfectant Doormat Works"
        description="A complete guide to smart disinfectant doormat technology: how SaniQ's pressure sensors, automated spray disinfection, and real-time monitoring eliminate 99.9% of shoe-sole pathogens in 2-3 seconds."
        path="/features"
        schema={[
          webPageSchema({ title: "How SaniQ's Smart Disinfectant Doormat Works", description: 'Smart disinfectant doormat technology explained.', path: '/features' }),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Features', path: '/features' }]),
          faqSchema(faqItems),
        ]}
      />
      <NavBar />
      <Breadcrumbs trail={[{ name: 'Home', path: '/' }, { name: 'Features', path: '/features' }]} />

      <header className="px-8 pb-12 pt-8 max-w-3xl mx-auto text-center">
        <p className="text-cyan-400 font-mono text-[10px] tracking-[0.4em] uppercase mb-4">Technology</p>
        <h1 className="text-4xl md:text-6xl font-extralight tracking-tighter text-white mb-6">
          Smart Disinfectant Doormat Technology: How SaniQ Works
        </h1>
        <p className="text-white/45 text-sm md:text-base leading-relaxed max-w-xl mx-auto">
          SaniQ eliminates 99.9% of germs, bacteria, and viruses from shoe soles in 2-3 seconds, automatically. Here's exactly how the technology works, how it compares to traditional and UV alternatives, and where it's designed to be used.
        </p>
      </header>

      {/* What is */}
      <section className="py-12 px-8 max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-light text-white mb-8">What Is a Smart Disinfectant Doormat?</h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Definition</h3>
            <p className="text-white/50 text-sm leading-relaxed">
              A smart disinfectant doormat is an entry-point device that combines sensors, automated disinfectant delivery, and (in SaniQ's case) connected monitoring to actively sanitize shoe soles the moment someone steps on it, as opposed to a traditional mat, which only physically traps loose debris.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white mb-2">How It Works</h3>
            <p className="text-white/50 text-sm leading-relaxed">
              An ultra-slim mat with precision pressure sensors activates the moment someone steps on it. Disinfectant is delivered only where it's needed, exactly when it's needed, targeting the exact contact area of the sole rather than flooding the whole mat surface.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Why It Matters</h3>
            <p className="text-white/50 text-sm leading-relaxed">
              96% of shoe soles carry harmful bacteria picked up from public spaces. Every one of those steps carries that contamination across the floor of a home, clinic, or workplace. A device that intervenes at the door, automatically and with no change in behaviour required, closes a hygiene gap that mopping, vacuuming, and hand sanitizer never reach.
            </p>
          </div>
        </div>
      </section>

      {/* Types */}
      <section className="py-12 px-8 max-w-5xl mx-auto border-t border-white/10">
        <h2 className="text-2xl md:text-3xl font-light text-white text-center mb-12">Types of Entryway Disinfection Technology</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass rounded-2xl p-6 border border-cyan-500/20">
            <h3 className="text-white text-base font-medium mb-2">Automated Spray Disinfection Mats</h3>
            <p className="text-white/45 text-sm leading-relaxed">SaniQ's approach: pressure sensors trigger targeted, medical-grade liquid disinfectant delivery onto the exact sole contact area within 2-3 seconds.</p>
          </div>
          <div className="glass rounded-2xl p-6 border border-white/10">
            <h3 className="text-white text-base font-medium mb-2">UV-C Sanitizing Mats</h3>
            <p className="text-white/45 text-sm leading-relaxed">Use ultraviolet light exposure to degrade pathogens. Effectiveness depends heavily on dwell time and direct line-of-sight to the sole surface, which textured treads can block.</p>
          </div>
          <div className="glass rounded-2xl p-6 border border-white/10">
            <h3 className="text-white text-base font-medium mb-2">Passive Antimicrobial Mats</h3>
            <p className="text-white/45 text-sm leading-relaxed">Coated with antimicrobial materials that slow bacterial growth on the mat itself, but do not actively disinfect the shoe sole on contact.</p>
          </div>
          <div className="glass rounded-2xl p-6 border border-white/10">
            <h3 className="text-white text-base font-medium mb-2">Hybrid Systems</h3>
            <p className="text-white/45 text-sm leading-relaxed">Combine mechanical trapping (bristles, grooves) with a secondary disinfection stage, generally slower and bulkier than a single-stage automated mat.</p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 px-8 max-w-6xl mx-auto border-t border-white/10">
        <h2 className="text-2xl md:text-3xl font-light text-white text-center mb-12">Benefits of Smart Disinfectant Doormats</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {[
            { t: 'Medical-Grade Pathogen Elimination', d: 'Kills 99.9% of germs and viruses, including E. coli, with clinical-strength disinfectant.' },
            { t: 'Zero-Effort Automation', d: 'Step on, sanitise, step off, with no buttons, sprays to operate, or behaviour change required.' },
            { t: 'Real-Time Hygiene Monitoring', d: 'Connected sensors can flag when maintenance or refills are needed, instead of guessing.' },
            { t: 'Reduced Indoor Transmission', d: 'Cuts one of the main vectors carrying outdoor pathogens and allergens across indoor floors.' },
            { t: 'Consistent, Repeatable Results', d: 'Every step gets the same targeted treatment, unlike mopping schedules or inconsistent shoe-removal habits.' },
          ].map((b) => (
            <div key={b.t} className="glass p-8 rounded-3xl border border-white/10">
              <h3 className="text-base font-medium text-white mb-3">{b.t}</h3>
              <p className="text-white/45 text-sm leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Challenges */}
      <section className="py-12 px-8 max-w-5xl mx-auto border-t border-white/10">
        <h2 className="text-2xl md:text-3xl font-light text-white text-center mb-12">Challenges of Smart Disinfectant Doormats</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { t: 'Upfront Cost', d: 'Automated hardware costs more than a woven mat, and the case for it rests on the hygiene gap it closes, not price parity.' },
            { t: 'Maintenance & Refill Logistics', d: 'Disinfectant cartridges need periodic replacement, which real-time monitoring is designed to simplify.' },
            { t: 'Outdoor Weatherproofing', d: 'Rain, dust, and temperature swings put real engineering demands on any electronic entryway device.' },
            { t: 'Adoption & Trust', d: 'As a new product category, building confidence in efficacy and safety data matters as much as the hardware itself.' },
          ].map((c) => (
            <div key={c.t} className="glass p-6 rounded-2xl border border-white/10">
              <h3 className="text-base font-medium text-white mb-2">{c.t}</h3>
              <p className="text-white/45 text-sm leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works: steps + 3D showcase */}
      <section className="py-12 border-t border-white/10">
        <div className="px-8 max-w-4xl mx-auto mb-4">
          <h2 className="text-2xl md:text-3xl font-light text-white text-center mb-4">How SaniQ Works, Step by Step</h2>
        </div>
        <div className="px-8 max-w-3xl mx-auto grid grid-cols-1 gap-4 mb-4">
          {[
            { t: 'Step 1: Step On', d: 'The pressure-sensor array detects contact the instant a shoe touches the mat surface.' },
            { t: 'Step 2: Intelligent Targeting', d: 'Sensors calculate the exact placement and shape of the sole in contact with the mat.' },
            { t: 'Step 3: Automated Spray Delivery', d: 'Medical-grade disinfectant is released only at the contact area, with no waste and no flooding of the mat.' },
            { t: 'Step 4: 2–3 Second Disinfection', d: 'The full cycle completes within a normal walking pace, eliminating 99.9% of targeted pathogens.' },
            { t: 'Step 5: Real-Time Monitoring', d: 'The system logs cycles and disinfectant levels, ready to flag maintenance needs before performance drops.' },
          ].map((s, i) => (
            <div key={s.t} className="glass rounded-2xl p-5 border border-white/10 flex gap-4 items-start">
              <span className="text-cyan-400 font-mono text-sm shrink-0 pt-0.5">{String(i + 1).padStart(2, '0')}</span>
              <div>
                <p className="text-white text-sm font-medium mb-1">{s.t}</p>
                <p className="text-white/45 text-xs leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
        <ShowcaseSection />
      </section>

      {/* Applications */}
      <section className="py-12 px-8 max-w-6xl mx-auto border-t border-white/10">
        <h2 className="text-2xl md:text-3xl font-light text-white text-center mb-12">Applications &amp; Use Cases</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {[
            { t: 'Residential Homes', d: 'Families with young children, elderly households, and allergy sufferers benefit from reduced indoor contamination.' },
            { t: 'Workplaces & Offices', d: 'High foot-traffic entryways are a shared hygiene risk across every employee and visitor.' },
            { t: 'Healthcare Facilities', d: 'Clinics and hospitals require stringent entry-point infection control beyond what a standard mat provides.' },
            { t: 'Childcare Centres', d: 'Young children are more vulnerable to pathogen exposure; entryway disinfection adds a layer of protection.' },
            { t: 'Hospitality & Restaurants', d: 'Food-service entryways are a documented vector for cross-contamination from outdoor surfaces.' },
          ].map((u) => (
            <div key={u.t} className="glass p-6 rounded-2xl border border-white/10">
              <h3 className="text-base font-medium text-white mb-2">{u.t}</h3>
              <p className="text-white/45 text-sm leading-relaxed">{u.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="py-12 px-8 max-w-5xl mx-auto border-t border-white/10">
        <h2 className="text-2xl md:text-3xl font-light text-white text-center mb-10">SaniQ vs. Traditional Doormats</h2>
        <h3 className="text-base font-medium text-white mb-4">Pros &amp; Cons</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <div className="glass rounded-2xl p-6 border border-white/10">
            <p className="text-white text-sm font-medium mb-3">Traditional Doormat</p>
            <p className="text-cyan-400 text-xs uppercase tracking-widest mb-1">Pros</p>
            <p className="text-white/45 text-xs leading-relaxed mb-3">Low cost, no maintenance, widely available.</p>
            <p className="text-cyan-400 text-xs uppercase tracking-widest mb-1">Cons</p>
            <p className="text-white/45 text-xs leading-relaxed">Traps only loose dirt, no germ-killing capability, gives a false sense of cleanliness.</p>
          </div>
          <div className="glass rounded-2xl p-6 border border-cyan-500/20">
            <p className="text-white text-sm font-medium mb-3">SaniQ</p>
            <p className="text-cyan-400 text-xs uppercase tracking-widest mb-1">Pros</p>
            <p className="text-white/45 text-xs leading-relaxed mb-3">99.9% pathogen elimination, fully automatic, real-time monitoring, weatherproof.</p>
            <p className="text-cyan-400 text-xs uppercase tracking-widest mb-1">Cons</p>
            <p className="text-white/45 text-xs leading-relaxed">Higher upfront cost, requires periodic disinfectant refills.</p>
          </div>
        </div>

        <h3 className="text-base font-medium text-white mb-4">Feature Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs md:text-sm border-collapse min-w-[480px]">
            <thead>
              <tr className="border-b border-white/10 text-white/60 uppercase tracking-widest text-[10px]">
                <th className="py-3 pr-4 font-medium">Feature</th>
                <th className="py-3 pr-4 font-medium">Traditional Mat</th>
                <th className="py-3 font-medium text-cyan-400">SaniQ</th>
              </tr>
            </thead>
            <tbody className="text-white/50">
              {[
                ['Kills bacteria & viruses', 'No', 'Yes, 99.9%'],
                ['Automatic operation', 'N/A', 'Yes'],
                ['Disinfection time', 'N/A', '2–3 seconds'],
                ['Real-time monitoring', 'No', 'Yes'],
                ['Weatherproof rating', 'Varies', 'IP67'],
              ].map((row) => (
                <tr key={row[0]} className="border-b border-white/5">
                  <td className="py-3 pr-4 text-white/70">{row[0]}</td>
                  <td className="py-3 pr-4">{row[1]}</td>
                  <td className="py-3 text-cyan-400">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Stats & trends */}
      <section className="py-12 px-8 max-w-4xl mx-auto border-t border-white/10">
        <h2 className="text-2xl md:text-3xl font-light text-white mb-8">Statistics &amp; Industry Trends</h2>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Latest Statistics</h3>
            <p className="text-white/50 text-sm leading-relaxed">96% of shoe soles carry harmful bacteria from public spaces; SaniQ is engineered to eliminate 99.9% of that pathogen load in a 2-3 second cycle. See our <Link to="/blog/do-doormats-kill-bacteria" className="text-cyan-400 hover:text-cyan-300 transition-colors">shoe-sole bacteria research breakdown</Link> for sourcing.</p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Market Growth</h3>
            <p className="text-white/50 text-sm leading-relaxed">Smart home hygiene devices are a growing subset of the broader connected-home and IoT hygiene market, driven by post-pandemic awareness of surface and contact-based transmission.</p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Future Trends</h3>
            <p className="text-white/50 text-sm leading-relaxed">Expect entry-point hygiene devices to increasingly integrate with smart-home platforms, offering usage analytics and predictive maintenance alerts rather than operating as standalone hardware.</p>
          </div>
        </div>
      </section>

      {/* Expert insights */}
      <section className="py-12 px-8 max-w-4xl mx-auto border-t border-white/10">
        <h2 className="text-2xl md:text-3xl font-light text-white mb-8">Expert Insights</h2>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Practical Recommendations</h3>
            <p className="text-white/50 text-sm leading-relaxed">Position an entryway disinfection device at every primary entrance, not just the front door, since side and garage entries are common blind spots in a household hygiene routine.</p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Common Mistakes to Avoid</h3>
            <p className="text-white/50 text-sm leading-relaxed">Relying solely on a passive mat and assuming visible cleanliness equals hygiene; skipping disinfectant refill schedules; placing a mat where foot traffic can bypass it.</p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Industry Standards</h3>
            <p className="text-white/50 text-sm leading-relaxed">Relevant Australian and international disinfectant and electrical safety standards SaniQ is being developed against will be listed here ahead of launch.</p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Compliance &amp; Security</h3>
            <p className="text-white/50 text-sm leading-relaxed">Certification and data-handling compliance details for the connected monitoring features will be published ahead of launch.</p>
          </div>
        </div>
      </section>

      <FAQSection heading="Frequently Asked Questions" items={faqItems} />

      {/* Related resources */}
      <section className="py-12 px-8 max-w-4xl mx-auto border-t border-white/10">
        <h2 className="text-2xl md:text-3xl font-light text-white mb-8">Related Resources</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/blog/do-doormats-kill-bacteria" className="glass rounded-2xl p-5 border border-white/10 hover:border-cyan-500/30 transition-colors">
            <p className="text-cyan-400 text-[10px] uppercase tracking-widest font-bold mb-2">Blog</p>
            <p className="text-white text-sm">Do Doormats Kill Bacteria? What Shoe-Sole Studies Actually Show</p>
          </Link>
          <Link to="/blog/uv-vs-spray-disinfection" className="glass rounded-2xl p-5 border border-white/10 hover:border-cyan-500/30 transition-colors">
            <p className="text-cyan-400 text-[10px] uppercase tracking-widest font-bold mb-2">Blog</p>
            <p className="text-white text-sm">UV Sanitizer vs. Spray Disinfection for Entryways</p>
          </Link>
          <Link to="/about" className="glass rounded-2xl p-5 border border-white/10 hover:border-cyan-500/30 transition-colors">
            <p className="text-cyan-400 text-[10px] uppercase tracking-widest font-bold mb-2">Company</p>
            <p className="text-white text-sm">About SaniQ</p>
          </Link>
          <Link to="/waitlist" className="glass rounded-2xl p-5 border border-white/10 hover:border-cyan-500/30 transition-colors">
            <p className="text-cyan-400 text-[10px] uppercase tracking-widest font-bold mb-2">Get Started</p>
            <p className="text-white text-sm">Join the Waitlist for 2027 Launch</p>
          </Link>
        </div>
      </section>

      <section className="py-12 px-8 max-w-4xl mx-auto border-t border-white/10">
        <h2 className="text-2xl md:text-3xl font-light text-white mb-4">Conclusion</h2>
        <p className="text-white/50 text-sm leading-relaxed">
          Smart disinfectant doormats close a hygiene gap that traditional mats were never built to address. SaniQ's automated, sensor-driven approach delivers medical-grade disinfection in the time it takes to walk through a door, with no behaviour change, no manual effort, and measurable results at 99.9% pathogen elimination.
        </p>
      </section>

      <section className="py-16 px-8 text-center">
        <div className="max-w-2xl mx-auto glass rounded-3xl p-10">
          <h2 className="text-2xl md:text-3xl font-light text-white mb-4">See SaniQ before launch.</h2>
          <p className="text-white/45 text-sm mb-8">Join the waitlist for launch-day access and founding-member pricing.</p>
          <button
            type="button"
            onClick={openWaitlistModal}
            className="px-10 py-4 bg-cyan-500 text-black font-bold rounded-full text-sm uppercase tracking-widest hover:scale-105 transition-transform"
          >
            Register
          </button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Features;
